// Tag for graphql format
const gql = String.raw;

// Fragments
import { CommissionMakeTiersFragment, ModelYearsFragment } from "@PayPlans/api/fragments/commons";

const SimpleMakeTiersFragment = gql`
	fragment SimpleMakeTiers on SimpleMakeTiers {
		make
		tiers {
			...BonusesTiers
		}
	}
`;

const GrossStructureFragment = gql`
	fragment GrossStructure on GrossStructure {
		make
		checked
		dealerCodeFS
	}
`;

const VariableGrossStructureFragment = gql`
	fragment VariableGrossStructure on VariableGrossStructure {
		newFinance
		preOwnedFinance
	}
`;

const DefaultCommissionsFragment = gql`
	fragment DefaultCommissions on CommissionGrossStructureBased {
		newVehicles {
			makes {
				...CommissionMakeTiers
			}
			variableGrossStructure {
				...GrossStructure
			}
		}
		preOwned {
			...CommissionMakeTiers
		}
		finance {
			makes {
				...CommissionMakeTiers
			}
			combinedOEMsGrossStructure {
				...GrossStructure
			}
			variableGrossStructure {
				...VariableGrossStructure
			}
			newAndPreOwnedRequired
			combineOEMs
			globalUnitCounts
		}
		newAndPreOwnedRequired
		combineFranchises
		globalUnitCounts
		isGuaranteedDraw
	}
	${CommissionMakeTiersFragment}
	${GrossStructureFragment}
	${VariableGrossStructureFragment}
`;

const CrossCommissionsFragment = gql`
	fragment CrossCommissions on CrossCommissions {
		make
		tiers {
			...CommissionsTiers
		}
		individual
		applyTiersRetroactively
	}
`;

const CrossVariableGrossStructureFragment = gql`
	fragment CrossVariableGrossStructure on CrossVariableGrossStructure {
		make
		checked
	}
`;

const DefaultItemTiersFragment = gql`
	fragment DefaultItemTiers on DefaultItemTiers {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		commission {
			...InputValue
		}
		addCommission {
			...InputValue
		}
	}
`;

const MiniDealCommissionsFragment = gql`
	fragment MiniDealCommissions on MiniDealCommissionsDefinition {
		make
		model
		modelYears {
			...ModelYears
		}
		tiers {
			...DefaultItemTiers
		}
		applyGrossRetroactively
		capEnabled
		cap
	}
	${ModelYearsFragment}
	${DefaultItemTiersFragment}
`;

const SimpleCrossStoreCommissionFragment = gql`
	fragment SimpleCrossStoreCommission on SimpleCrossStoreCommission {
		commissions {
			...CrossCommissions
		}
		miniDeal {
			...MiniDealCommissions
		}
		variableGrossStructure {
			...CrossVariableGrossStructure
		}
	}
	${CrossCommissionsFragment}
	${MiniDealCommissionsFragment}
`;

const FinanceCrossStoreCommissionFragment = gql`
	fragment FinanceCrossStoreCommission on FinanceCrossStoreCommission {
		commissions {
			...CrossCommissions
		}
		variableGrossStructure {
			...CrossVariableGrossStructure
		}
		globalUnitCounts
	}
`;

const CrossStoreCommissionsFragment = gql`
	fragment CrossStoreCommissions on CrossStoreCommissions {
		newVehicles {
			...SimpleCrossStoreCommission
		}
		preOwned {
			...SimpleCrossStoreCommission
		}
		finance {
			...FinanceCrossStoreCommission
		}
		globalUnitCounts
	}
	${CrossVariableGrossStructureFragment}
	${FinanceCrossStoreCommissionFragment}
	${SimpleCrossStoreCommissionFragment}
`;

const FrequencyTiersFragment = gql`
	fragment FrequencyTiers on FrequencyTiers {
		frequency
		tiers {
			...BonusesTiers
		}
		applyTiersRetroactively
		isRetail
		isWholesale
		isFinance
		make
		activeFrequencies
	}
`;

const MarketMakeFragment = gql`
	fragment MarketMake on MarketMake {
		make
		frequency
		tiers {
			...BonusesTiers
		}
		applyTiersRetroactively
		isRetail
		isFleet
		isFinance
		activeFrequencies
	}
`;

const GrowthBonusFragment = gql`
	fragment GrowthBonus on GrowthBonus {
		makes {
			...MarketMake
		}
		preOwned {
			...FrequencyTiers
		}
		newAndPreOwnedRequired
		combineFranchises
	}
	${FrequencyTiersFragment}
`;

const MarketShareFragment = gql`
	fragment MarketShare on MarketShare {
		makes {
			...MarketMake
		}
		simultaneousMarketShare
	}
`;

const MetricLevelFragment = gql`
	fragment MetricLevel on MetricLevel {
		metricName
		metricLevel
		required
	}
`;

const PinnacleFragment = gql`
	fragment Pinnacle on Pinnacle {
		oemReference
		version
		metrics {
			...MetricLevel
		}
		tiers {
			...MetricTierItem
		}
	}
	${MetricLevelFragment}
`;

const RankingBonusFragment = gql`
	fragment RankingBonus on RankingBonus {
		makes {
			...SimpleMakeTiers
		}
		simultaneousRanking
	}
	${SimpleMakeTiersFragment}
`;

const ComplexMakeMetricsFragment = gql`
	fragment ComplexMakeMetrics on ComplexMakeMetrics {
		make
		oemReference
		version
		potentialPayout
		manualInput {
			...InputValue
		}
		metrics {
			...Metric
		}
		tiers {
			...MetricTierItem
		}
	}
`;

const SFEBonusFragment = gql`
	fragment SFEBonus on SFEBonus {
		makeMetrics {
			...ComplexMakeMetrics
		}
	}
	${ComplexMakeMetricsFragment}
`;

// THIS PURPOSEFULLY DOES NOT INCLUDE THE TIER FRAGMENT
const OperatingCostsMakeFragment = gql`
	fragment OperatingCostsMake on OperatingCostsMake {
		_id
		make
		dealerCodeFS
		frequency
		tiers {
			...CommissionsTiers
		}
		applyTiersRetroactively
	}
`;

const OperatingCostsFragment = gql`
	fragment OperatingCosts on OperatingCosts {
		makes {
			...OperatingCostsMake
		}
		combineOEMs
	}
	${OperatingCostsMakeFragment}
`;

export {
	CrossStoreCommissionsFragment,
	DefaultCommissionsFragment,
	GrowthBonusFragment,
	MarketShareFragment,
	PinnacleFragment,
	RankingBonusFragment,
	SFEBonusFragment,
	MarketMakeFragment,
	OperatingCostsFragment,
};
